import { type SDK_Fonts, strings } from "@quickpoint.me/shared";
import { CurrentFilter, FilterFonts } from "../../state/filter";
import { PageSizeAtom } from "../../state/page";
import {
	ConstrainDimensionsSelector,
	EditMode,
	IsPreviewGenerator,
} from "../../state/ui";

import gsap from "gsap";
import { useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import styled, { ThemeProvider } from "styled-components";
import useStickyAnimation from "../../lib/animations/useStickyAnimation";
import type { GridObject } from "../../lib/calculateGridRowsAndColumns";
import { mainCssRules } from "../../lib/cssTemplates";
import { setMetaTagColor } from "../../lib/setColorMetaTag";
import Cell from "./Cell";
import { GridContext } from "./DisplayElements";
import { getStartingPoint } from "./RenderGrid";

const StyledStickyOuter = styled.header<{
	$baseFontSize: number;
	$baseFontSizeRaw: number;
	$isPreviewer: boolean;
	$constrainDimensions: boolean;
	$fonts: SDK_Fonts;
	$isEmpty: boolean;
	$position: "top" | "bottom";
}>`
	visibility: hidden; /* Initially hidden, GSAP will manage visibility */
	width: ${(props) => (props.$constrainDimensions ? "100%" : "100vw")};
	width: ${(props) => (props.$constrainDimensions ? "100%" : "100svw")};
	${(props) =>
		`${mainCssRules({
			baseFontSize: props.$baseFontSize,
			baseFontSizeRaw: props.$baseFontSizeRaw,
			fonts: props.$fonts,
			theme:
				props.$position === "top"
					? props.theme.stickyHeader
					: props.theme.stickyFooter,
		})}`};
`;

const StyledSticky = styled.div<{
	$maxCols: number;
	width: number;
}>`
	align-items: ${(props) => getStartingPoint(props.theme.startingPoint)};
	display: grid;
	grid-template-columns: ${(props) => `repeat(${props.$maxCols || 1}, 1fr)`};
	position: relative;
	margin: auto;
	width: ${(props) => props.width}px;
	pointer-events: auto;
`;

/**
 * @param gridArray : A 2 or 3d array representing a grid with one row or many rows
 *
 * @returns array of JSX Elements with the correct Grid CSS applied.
 */
function RenderSticky({
	gridArray,
	position,
}: {
	gridArray: GridObject[];
	position: "top" | "bottom";
}) {
	const pageSize = useRecoilValue(PageSizeAtom);
	const inEditMode = useRecoilValue(EditMode);
	const isPreviewer = useRecoilValue(IsPreviewGenerator);
	const baseFontSize = pageSize.baseFontSize;
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const fonts = useRecoilValue(FilterFonts);
	const isEmpty = gridArray.length === 0;
	const filter = useRecoilValue(CurrentFilter);
	const stickyRef = useRef<HTMLDivElement>(null);

	useStickyAnimation({ elRef: stickyRef, isEmpty });

	// Safeguard to kill animations on unmount
	useEffect(() => {
		const element = stickyRef.current;
		return () => {
			if (element) {
				gsap.killTweensOf(element);
			}
		};
	}, []);

	const metaColor = isEmpty
		? filter.background
		: position === "top"
			? filter.stickyHeader.background
			: filter.stickyFooter.background;

	useEffect(() => {
		if (!constrainDimensions) setMetaTagColor(metaColor);
	}, [constrainDimensions, metaColor]);

	const cells = gridArray.map((gridObject, idx) => {
		return (
			<Cell
				gridObject={gridObject}
				idx={idx}
				isPreviewer={isPreviewer}
				inEditMode={inEditMode}
				alignCellAsColumn={true}
				key={`cell-${gridObject.row}-${gridObject.col}`}
			/>
		);
	});

	const gridRef = useRef<HTMLDivElement>(null);
	const numberOfColumns = gridArray.reduce((prev, cell) => {
		return cell.col > prev ? cell.col : prev;
	}, 0);
	return (
		<ThemeProvider theme={filter}>
			<StyledStickyOuter
				ref={stickyRef}
				as={position === "bottom" ? "footer" : "header"}
				$isEmpty={isEmpty}
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$constrainDimensions={constrainDimensions}
				$fonts={fonts}
				$isPreviewer={isPreviewer}
				$position={position}
				data-sticky-position={position}
			>
				<StyledSticky
					ref={gridRef}
					className={`${strings.css.stickyHeader} `}
					$maxCols={numberOfColumns}
					width={pageSize.width}
				>
					<GridContext.Provider value={true}>{cells}</GridContext.Provider>
				</StyledSticky>
			</StyledStickyOuter>
		</ThemeProvider>
	);
}

export default RenderSticky;
